import React from 'react'
import { Text, View } from 'react-native'
import { Block, BlockComponentProps } from '../../types'
import { colors, layout, space, typography } from '../../tokens'
import { mediaSupportsFullWidth } from './fullWidth'
import AudioPlayer from './AudioPlayer'
import { useDocletAssets } from '../../DocletAssetsContext'
import { playableMedia } from './playable'

// 16:9 box: height from percentage padding (relative to width), as for images
// -- react-native-web 0.15 drops the aspectRatio style
const videoBox = {
  width: '100%',
  paddingBottom: '56.25%',
  backgroundColor: colors.text
}

const fill = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%'
}

/**
 * The player alone: video at 16:9 with its thumbnail, in the browser's own
 * controls (download switched off), or our audio player. It plays the block's
 * host media record (playableMedia). While a new video is still transcoding
 * it shows a placeholder of the right size: the thumbnail, dimmed, once Vimeo
 * has one, with a message over it. Also used by the builder's editor, which passes a
 * fuller message than the public page's.
 */
export const MediaPlayer = ({
  block,
  processingMessage = 'Video is processing',
  failedMessage = null
}: {
  block: Block<'media'>
  processingMessage?: string
  // Builder only: shown for a video that failed (the page leaves it out)
  failedMessage?: string | null
}) => {
  const { kind, poster } = block.props
  const { hostMedia } = useDocletAssets()
  const { src, failed } = playableMedia(block.props, hostMedia)
  const placeholder = failed ? failedMessage : processingMessage
  if (failed && !placeholder) return null

  if (kind === 'audio') {
    return src ? <AudioPlayer src={src} /> : null
  }

  return (
    <View style={videoBox}>
      {src ? (
        <video
          controls
          controlsList="nodownload"
          playsInline
          preload="metadata"
          src={src}
          poster={poster ?? undefined}
          style={{ ...fill, objectFit: 'contain', backgroundColor: '#000' }}
        />
      ) : (
        <>
          {!!poster && !failed && (
            <img
              src={poster}
              alt=""
              style={{ ...fill, objectFit: 'contain', opacity: 0.35 }}
            />
          )}
          <View
            style={[
              fill,
              {
                alignItems: 'center',
                justifyContent: 'center',
                paddingHorizontal: space[6]
              }
            ]}
          >
            <Text
              style={{
                ...typography.caption,
                marginTop: 0,
                color: colors.page,
                textAlign: 'center'
              }}
            >
              {placeholder}
            </Text>
          </View>
        </>
      )}
    </View>
  )
}

export const mediaBlockStyle = { marginBottom: space[4] }

// A full-width video runs to the page's edges; its caption stays in line with
// the text, as an image's does
export const mediaCaptionStyle = (block: Block<'media'>) => ({
  ...typography.caption,
  ...(block.fullWidth && mediaSupportsFullWidth(block.props)
    ? { paddingHorizontal: layout.blockPadding }
    : {})
})

// Legacy equivalents: embededaudio, embededvideo and video. A block with no
// media yet, or whose video failed, is left out of the rendered page.
const MediaBlock = ({ block }: BlockComponentProps<'media'>) => {
  const { kind, caption } = block.props
  const { hostMedia } = useDocletAssets()
  // No media yet, or a video that failed: leave out the block, caption too
  if (!kind || playableMedia(block.props, hostMedia).failed) return null

  return (
    <View style={mediaBlockStyle}>
      <MediaPlayer block={block} />
      {!!caption.trim() && (
        <Text style={mediaCaptionStyle(block)}>{caption}</Text>
      )}
    </View>
  )
}

export default MediaBlock
